import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { Transition } from "../../../../../utils/ModalHelpers";
import {
  getDiagnosticFlowById,
  getDiagnosticFlowTranslations,
  saveDiagnosticFlowTranslation,
  deleteDiagnosticFlowTranslation,
} from "../services/diagnosticFlowsApiService";
import { deserializeFlow } from "../utils/flowSerializer";
import { parseTranslationBlob, mergeNodeIntoLanguageBlob } from "../utils/translationBlob";
import { TRANSLATION_LANGUAGES } from "../utils/translationConstants";
import { NodeField } from "../utils/flowSchema";
import { mapFlowToTranslationRows } from "../mappers/diagnosticFlowTranslationsMapper";
import DiagnosticFlowTranslationsTable from "./DiagnosticFlowTranslationsTable";

export default function DiagnosticFlowTranslations({
  open,
  flowId,
  filterNodeId,
  fullScreen = true,
  hasTranslationLicence,
  resources = {},
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  // Full per-language blobs from load time; per-node saves merge into these.
  const fullBlobsRef = useRef({});

  useEffect(() => {
    if (!open || !flowId) return undefined;

    let active = true;
    setLoading(true);
    (async () => {
      const detail = await getDiagnosticFlowById(flowId, resources);
      const translations = await getDiagnosticFlowTranslations(flowId);
      if (!active) return;

      if (detail) {
        const { nodes } = deserializeFlow(detail.jsonFlow);
        const base = {
          name: detail.name,
          information: detail.information,
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type,
            questionType: n.data.questionType,
            label: n.data.label,
            prompt: n.data.prompt,
            information: n.data.information,
            [NodeField.LIST_OPTIONS]: n.data[NodeField.LIST_OPTIONS],
          })),
        };

        const blobByLang = {};
        (translations || []).forEach((t) => {
          blobByLang[t.language] = parseTranslationBlob(t.jsonTranslation);
        });
        fullBlobsRef.current = blobByLang;

        let mapped = mapFlowToTranslationRows(base, blobByLang, TRANSLATION_LANGUAGES);
        if (filterNodeId) {
          mapped = mapped.filter((row) => row.nodeId === filterNodeId);
        }
        setRows(mapped);
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [open, flowId, filterNodeId]);

  const handleSaveLanguage = async (language, blob) => {
    const payload = filterNodeId
      ? mergeNodeIntoLanguageBlob(fullBlobsRef.current[language], filterNodeId, blob)
      : blob;
    const isEmpty =
      Object.keys(payload.flow || {}).length === 0 &&
      Object.keys(payload.nodes || {}).length === 0;
    const ok = isEmpty
      ? await deleteDiagnosticFlowTranslation(flowId, language)
      : await saveDiagnosticFlowTranslation(flowId, language, payload, resources);
    if (ok) fullBlobsRef.current[language] = isEmpty ? undefined : payload;
    return ok;
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      open={!!open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" style={{ flex: 1 }}>
            {resources["Translations"]}
          </Typography>
          <IconButton edge="end" onClick={onClose} id="close-diagnostic-flow-translations">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3} style={{ maxWidth: "1000px", width: "100%", margin: "0 auto" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress />
          </Box>
        ) : (
          <DiagnosticFlowTranslationsTable
            rows={rows}
            languages={TRANSLATION_LANGUAGES}
            resources={resources}
            onSaveLanguage={handleSaveLanguage}
            hasTranslationLicence={hasTranslationLicence}
          />
        )}
      </Box>
    </Dialog>
  );
}
